import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { GALLERY, CASE_STUDIES } from "@/lib/data";

const FILTERS = ["All", "Full Home", "Modular Kitchen", "Wardrobe"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const items = filter === "All" ? GALLERY : GALLERY.filter((g) => g.tag === filter);

  return (
    <main data-testid="gallery-page" className="pt-32 pb-28 relative z-10">
      <Seo
        title="Interior Design Portfolio in Kolkata | Projects — ZEBO"
        description="Explore ZEBO's portfolio of full-home interiors, modular kitchens and wardrobes across Kolkata — Ballygunge, Salt Lake, New Town, Rajdanga and more."
        path="/gallery"
      />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="overline text-[#8B0000] mb-4">Portfolio</p>
          <h1 className="font-serif text-6xl sm:text-7xl lg:text-8xl font-light tracking-tight text-[#143D63] leading-[0.95]">
            Spaces we&apos;ve<br />brought to life
          </h1>
          <p className="text-[#143D63]/55 text-sm mt-6 max-w-xl" data-testid="gallery-imagery-note">
            Photography shown is representative concept imagery — scopes, locations and timelines reflect real delivered ZEBO projects.
          </p>
        </Reveal>

        <div className="flex flex-wrap gap-3 mt-12 mb-12">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase().replace(" ", "-")}`}
              onClick={() => setFilter(f)}
              className={`px-5 py-2.5 text-sm font-medium border transition-colors duration-300 ${
                filter === f
                  ? "bg-[#143D63] text-[#F9F8F6] border-[#143D63]"
                  : "bg-transparent text-[#143D63] border-[#143D63]/25 hover:border-[#143D63]"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {items.map((g, i) => (
            <motion.div
              key={g.title}
              layout
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
              className={g.span}
            >
              {CASE_STUDIES[g.slug] ? (
                <Link to={`/projects/${g.slug}`} data-testid={`gallery-card-${g.slug}`} className="group relative overflow-hidden aspect-[16/11] block">
                  <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/75 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 p-7">
                    <p className="overline text-[#F8C21C] mb-1">{g.tag}</p>
                    <h3 className="font-serif text-3xl text-[#F9F8F6]">{g.title}</h3>
                    <span className="text-[#F9F8F6]/80 text-sm mt-2 inline-block opacity-0 group-hover:opacity-100 transition-opacity duration-300">View project &rarr;</span>
                  </div>
                </Link>
              ) : (
                <div data-testid={`gallery-card-${g.slug}`} className="group relative overflow-hidden aspect-[16/11]">
                  <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/75 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 p-7">
                    <p className="overline text-[#F8C21C] mb-1">{g.tag}</p>
                    <h3 className="font-serif text-3xl text-[#F9F8F6]">{g.title}</h3>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
